import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import api from '../../api';
import './ConfigBaremePage.css';

const ALLY_ROWS = ['1 allié', '2 alliées', '3 alliées', '4 alliées', '5 alliées'];
const ENEMY_COLUMNS = ['0 ennemi', '1 ennemi', '2 ennemis', '3 ennemis', '4 ennemis', '5 ennemis'];

const toOptionalNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
};

const generateEmptyTable = () => {
    const tableau = {};

    ALLY_ROWS.forEach(ligne => {
        tableau[ligne] = {};
        ENEMY_COLUMNS.forEach(colonne => {
            tableau[ligne][colonne] = '';
        });
    });

    return tableau;
};

const normalizeTable = (table) => {
    const normalized = generateEmptyTable();

    if (!table || typeof table !== 'object') {
        return normalized;
    }

    ALLY_ROWS.forEach((row) => {
        ENEMY_COLUMNS.forEach((col) => {
            if (table?.[row] && Object.prototype.hasOwnProperty.call(table[row], col)) {
                normalized[row][col] = table[row][col];
            }
        });
    });

    return normalized;
};

const ConfigBarèmePage = () => {
    const { serverId } = useParams();
    const { t } = useLanguage();
    const { success, error, warning } = useNotification();

    const [barèmes, setBarèmes] = useState([]);
    const [selectedBarèmeId, setSelectedBarèmeId] = useState(null);
    const [barèmeName, setBarèmeName] = useState('');
    const [typeBarème, setTypeBarème] = useState('attaque');
    const [tableauValues, setTableauValues] = useState(generateEmptyTable());
    const [attackVsAllFixedPoints, setAttackVsAllFixedPoints] = useState('');
    const [defenseVsAllFixedPoints, setDefenseVsAllFixedPoints] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        if (!serverId) return;
        setLoading(true);
        api.get(`/baremes/${serverId}`)
            .then(res => {
                const data = res.data;
                if (Array.isArray(data)) {
                    setBarèmes(data);
                    if (data.length > 0 && !showCreateForm) {
                        const firstBareme = data[0];
                        setSelectedBarèmeId(firstBareme._id);
                        setTableauValues(normalizeTable(firstBareme.table));
                        setTypeBarème(firstBareme.type);
                        setBarèmeName(firstBareme.name);
                        setAttackVsAllFixedPoints(firstBareme.attackVsAllFixedPoints ?? '');
                        setDefenseVsAllFixedPoints(firstBareme.defenseVsAllFixedPoints ?? '');
                    } else {
                        resetForm();
                    }
                } else {
                    console.error("❌ Format inattendu :", data);
                }
            })
            .catch(err => console.error("❌ Erreur récupération barèmes :", err))
            .finally(() => setLoading(false));
    }, [serverId, showCreateForm]);

    useEffect(() => {
        if (!selectedBarèmeId || showCreateForm) return;

        const barème = barèmes.find(b => b._id === selectedBarèmeId);
        if (barème) {
            setBarèmeName(barème.name);
            setTypeBarème(barème.type);
            setTableauValues(normalizeTable(barème.table));
            setAttackVsAllFixedPoints(barème.attackVsAllFixedPoints ?? '');
            setDefenseVsAllFixedPoints(barème.defenseVsAllFixedPoints ?? '');
            setHasUnsavedChanges(false);
        }
    }, [selectedBarèmeId, barèmes, showCreateForm]);

    const resetForm = () => {
        setSelectedBarèmeId(null);
        setTableauValues(generateEmptyTable());
        setTypeBarème("attaque");
        setBarèmeName("");
        setAttackVsAllFixedPoints('');
        setDefenseVsAllFixedPoints('');
        setHasUnsavedChanges(false);
    };

    const handleInputChange = (ligne, colonne, value) => {
        setTableauValues(prev => ({
            ...prev,
            [ligne]: {
                ...prev[ligne],
                [colonne]: value || '0',
            }
        }));
        setHasUnsavedChanges(true);
    };

    const handleBaremeNameChange = (value) => {
        setBarèmeName(value);
        setHasUnsavedChanges(true);
    };

    const handleTypeChange = (value) => {
        setTypeBarème(value);
        setHasUnsavedChanges(true);
    };

    const handleAnyPointsChange = (field, value) => {
        if (field === 'attack') {
            setAttackVsAllFixedPoints(value);
        } else {
            setDefenseVsAllFixedPoints(value);
        }
        setHasUnsavedChanges(true);
    };

    const handleSave = async () => {
        if (!barèmeName.trim()) {
            warning(t('bareme.nameRequired', '⚠️ Veuillez renseigner un nom pour le barème !'));
            return;
        }

        setSaving(true);
        const baremeData = {
            name: barèmeName.trim(),
            type: typeBarème,
            table: tableauValues,
            attackVsAllFixedPoints: toOptionalNumber(attackVsAllFixedPoints),
            defenseVsAllFixedPoints: toOptionalNumber(defenseVsAllFixedPoints),
            serverId,
        };

        try {
            let response;
            if (selectedBarèmeId && !showCreateForm) {
                // Mise à jour
                response = await api.put(`/baremes/${selectedBarèmeId}`, baremeData);
                setBarèmes(prev => prev.map(b => b._id === selectedBarèmeId ? response.data : b));
                success(t('bareme.baremeUpdated', '✅ Barème mis à jour avec succès !'));
            } else {
                // Création
                response = await api.post('/save-bareme', baremeData);
                setBarèmes(prev => [...prev, response.data]);
                setSelectedBarèmeId(response.data._id);
                setShowCreateForm(false);
                success(t('bareme.baremeCreated', '✅ Barème créé avec succès !'));
            }
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error("❌ Erreur lors de l'enregistrement :", err);
            error(t('bareme.errorSaving', '❌ Erreur lors de l\'enregistrement !'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (baremeId, baremeName) => {
        if (window.confirm(t('bareme.confirmDelete', '⚠️ Voulez-vous vraiment supprimer le barème "{name}" ?\n\nCette action est irréversible !').replace('{name}', baremeName))) {
            try {
                await api.delete(`/baremes/${baremeId}`);
                setBarèmes(prev => prev.filter(b => b._id !== baremeId));

                if (selectedBarèmeId === baremeId) {
                    resetForm();
                    if (barèmes.length > 1) {
                        const remainingBaremes = barèmes.filter(b => b._id !== baremeId);
                        const nextBareme = remainingBaremes[0];
                        setSelectedBarèmeId(nextBareme._id);
                    }
                }
                success(t('bareme.baremeDeleted', '✅ Barème supprimé avec succès !'));
            } catch (err) {
                console.error("❌ Erreur suppression :", err);
                error(t('bareme.errorDeleting', '❌ Erreur lors de la suppression !'));
            }
        }
    };

    const discardChanges = () => {
        if (!hasUnsavedChanges) return;
        if (window.confirm(t('bareme.confirmUnsaved', '⚠️ Vous avez des modifications non sauvegardées. Les abandonner ?'))) {
            const bareme = barèmes.find(b => b._id === selectedBarèmeId);
            if (bareme) {
                setBarèmeName(bareme.name);
                setTypeBarème(bareme.type);
                setTableauValues(normalizeTable(bareme.table));
                setAttackVsAllFixedPoints(bareme.attackVsAllFixedPoints ?? '');
                setDefenseVsAllFixedPoints(bareme.defenseVsAllFixedPoints ?? '');
                setHasUnsavedChanges(false);
            }
        }
    };

    const filteredBaremes = barèmes.filter(bareme => {
        const matchesSearch = bareme.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' || bareme.type === typeFilter;
        return matchesSearch && matchesType;
    });

    if (loading) {
        return (
            <div className="c2-scope c2-admin-page">
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('bareme.loadingBaremes', 'Chargement des barèmes...')}</p>
                </div>
            </div>
        );
    }

    const attackCount = barèmes.filter(b => b.type === 'attaque').length;
    const defenseCount = barèmes.filter(b => b.type === 'défense').length;

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="scale"
                title={t('bareme.title', 'Configuration des Barèmes')}
                subtitle={t('bareme.subtitle', 'Configuration des points d\'attaque et de défense')}
            />

            <div className="bareme-meta">
                <span className="c2-chip">{barèmes.length} {barèmes.length > 1 ? t('bareme.baremesPlural', 'barèmes') : t('bareme.baremeSingular', 'barème')}</span>
                <span className="c2-chip c2-chip--green"><Icon name="swords" size={14} />{attackCount} {t('bareme.attack', 'attaque')}</span>
                <span className="c2-chip c2-chip--purple"><Icon name="shield" size={14} />{defenseCount} {t('bareme.defense', 'défense')}</span>
            </div>

            <div className="bareme-layout">
                {/* ── Liste des barèmes ── */}
                <aside className="c2-card bareme-list">
                    <div className="bareme-list__head">
                        <h2 className="c2-section__title">{t('bareme.existingBaremes', 'Barèmes existants')}</h2>
                        <button
                            type="button"
                            className="c2-btn c2-btn--primary c2-btn--sm"
                            onClick={() => {
                                setShowCreateForm(true);
                                resetForm();
                            }}
                        >
                            <Icon name="plus" size={16} />
                            {t('bareme.newBareme', 'Nouveau')}
                        </button>
                    </div>

                    <div className="bareme-list__filters">
                        <input
                            type="search"
                            className="c2-input c2-input--sm"
                            placeholder={t('bareme.search', 'Rechercher...')}
                            aria-label={t('bareme.search', 'Rechercher...')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            className="c2-select bareme-list__filter"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            aria-label={t('bareme.type', 'Type')}
                        >
                            <option value="all">{t('bareme.allTypes', 'Tous types')}</option>
                            <option value="attaque">{t('bareme.attack', 'Attaque')}</option>
                            <option value="défense">{t('bareme.defense', 'Défense')}</option>
                        </select>
                    </div>

                    <ul className="bareme-list__items">
                        {filteredBaremes.length > 0 ? (
                            filteredBaremes.map(bareme => {
                                const isSelected = selectedBarèmeId === bareme._id && !showCreateForm;
                                const isAttack = bareme.type === 'attaque';
                                return (
                                    <li key={bareme._id} className={`bareme-item${isSelected ? ' is-selected' : ''}`}>
                                        <button
                                            type="button"
                                            className="bareme-item__select"
                                            onClick={() => {
                                                setSelectedBarèmeId(bareme._id);
                                                setShowCreateForm(false);
                                            }}
                                            aria-current={isSelected ? 'true' : undefined}
                                        >
                                            <span className="bareme-item__name">{bareme.name}</span>
                                            <span className={`c2-badge ${isAttack ? 'c2-badge--green' : 'c2-badge--purple'}`}>
                                                <span className={`c2-diamond${isAttack ? '' : ' c2-diamond--purple'}`} />
                                                {bareme.type}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                            onClick={() => handleDelete(bareme._id, bareme.name)}
                                            title={t('common.delete', 'Supprimer')}
                                            aria-label={t('baremePage.deleteItem', 'Supprimer {name}', { name: bareme.name })}
                                        >
                                            <Icon name="trash" size={16} />
                                        </button>
                                    </li>
                                );
                            })
                        ) : (
                            <li className="c2-empty">
                                <Icon name="scale" size={32} />
                                <p>{barèmes.length === 0 ? t('bareme.noBaremes', 'Aucun barème créé') : t('bareme.noResults', 'Aucun résultat')}</p>
                            </li>
                        )}
                    </ul>
                </aside>

                {/* ── Édition ── */}
                <section className="c2-card c2-section bareme-editor">
                    <div className="c2-section__head">
                        <h2 className="c2-section__title">
                            <Icon name={showCreateForm ? 'plus' : 'pencil'} size={20} />
                            {showCreateForm ? t('bareme.createBareme', 'Créer un nouveau barème') : t('bareme.editBareme', 'Modifier le barème')}
                        </h2>
                        {hasUnsavedChanges && (
                            <span className="c2-chip bareme-unsaved">{t('bareme.unsavedChanges', 'Modifications non sauvegardées')}</span>
                        )}
                    </div>

                    <div className="bareme-editor__fields">
                        <label className="c2-field">
                            <span className="c2-field__label">{t('bareme.baremeName', 'Nom du barème')}</span>
                            <input
                                type="text"
                                className="c2-input"
                                placeholder={t('bareme.baremeNamePlaceholder', 'Ex: Barème GDC Été 2024')}
                                value={barèmeName}
                                onChange={(e) => handleBaremeNameChange(e.target.value)}
                            />
                        </label>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('bareme.type', 'Type')}</span>
                            <select
                                className="c2-select"
                                value={typeBarème}
                                onChange={(e) => handleTypeChange(e.target.value)}
                            >
                                <option value="attaque">{t('bareme.attack', 'Attaque')}</option>
                                <option value="défense">{t('bareme.defense', 'Défense')}</option>
                            </select>
                        </label>
                    </div>

                    <div className="bareme-grid-wrap">
                        <table className="bareme-grid">
                            <thead>
                                <tr>
                                    <th scope="col"><span className="visually-hidden">{t('baremePage.alliesEnemies', 'Alliés / ennemis')}</span></th>
                                    {['0', '1', '2', '3', '4', '5'].map((col) => (
                                        <th key={col} scope="col">
                                            {col} {col === '1' ? t('bareme.enemy', 'ennemi') : t('bareme.enemies', 'ennemis')}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(tableauValues).map((ligne) => (
                                    <tr key={ligne}>
                                        <th scope="row">{ligne}</th>
                                        {ENEMY_COLUMNS.map((colonne) => (
                                            <td key={colonne}>
                                                <input
                                                    className="bareme-cell"
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={tableauValues[ligne][colonne]}
                                                    onChange={(e) => handleInputChange(ligne, colonne, e.target.value)}
                                                    placeholder="0"
                                                    aria-label={`${ligne}, ${colonne}`}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="bareme-fixed">
                        <h3>{t('baremePage.fixedTitle', 'Points fixes « contre tous »')} <span className="c2-muted">{t('baremePage.optional', '(optionnel)')}</span></h3>
                        <div className="bareme-editor__fields">
                            <label className="c2-field">
                                <span className="c2-field__label">{t('baremePage.fixedDefense', 'Points fixes Défense contre tous')}</span>
                                <input
                                    type="number"
                                    min="0"
                                    className="c2-input"
                                    value={defenseVsAllFixedPoints}
                                    onChange={(e) => handleAnyPointsChange('defense', e.target.value)}
                                    placeholder={t('baremePage.example25', 'Ex: 25')}
                                />
                            </label>
                            <label className="c2-field">
                                <span className="c2-field__label">{t('baremePage.fixedAttack', 'Points fixes Attaque contre tous')}</span>
                                <input
                                    type="number"
                                    min="0"
                                    className="c2-input"
                                    value={attackVsAllFixedPoints}
                                    onChange={(e) => handleAnyPointsChange('attack', e.target.value)}
                                    placeholder={t('baremePage.example25', 'Ex: 25')}
                                />
                            </label>
                        </div>
                    </div>

                    <div className="bareme-actions">
                        {!showCreateForm && selectedBarèmeId && (
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={discardChanges} disabled={!hasUnsavedChanges}>
                                <Icon name="refresh" size={18} />
                                {t('bareme.reset', 'Annuler')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="c2-btn c2-btn--primary"
                            onClick={handleSave}
                            disabled={saving || !hasUnsavedChanges}
                        >
                            <Icon name={showCreateForm ? 'plus' : 'save'} size={18} />
                            {saving ? t('bareme.saving', 'Sauvegarde...') : showCreateForm ? t('bareme.create', 'Créer') : t('bareme.save', 'Sauvegarder')}
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ConfigBarèmePage;
